import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ReasoningTestPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const handleStart = () => user ? navigate('/test-zone') : navigate('/login');

  const faqs = [
    { q: 'Is the reasoning test free?', a: 'Yes, you can attempt reasoning tests completely free.' },
    { q: 'What type of questions are included?', a: 'Logical reasoning, analytical reasoning, and problem-solving questions.' },
    { q: 'Are tests suitable for beginners?', a: 'Yes, tests are available from easy to advanced levels.' },
    { q: 'Will I get results after the test?', a: 'Yes, instant score and analysis are provided immediately.' },
    { q: 'Do I need to register?', a: 'Yes, quick registration helps track your performance over time.' },
  ];

  return (
    <div className="min-h-screen pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
        <div className="text-center max-w-4xl mx-auto">
          <div className="hero-badge mx-auto mb-6">🧠 Reasoning Zone</div>
          <h1 className="text-4xl lg:text-6xl font-extrabold gradient-text mb-6 leading-tight">Free Reasoning Test Online</h1>
          <p className="text-xl text-text-muted mb-4">Sharpen your logical thinking with free reasoning tests designed for all competitive exams</p>
          <p className="text-text-muted mb-8">✅ Free Practice &nbsp;•&nbsp; ⚡ Instant Results &nbsp;•&nbsp; 📊 All Levels Covered</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={handleStart} className="btn-primary px-8 py-4 text-lg font-bold rounded-xl">🚀 Start Free Reasoning Test</button>
            <Link to="/register" className="px-8 py-4 text-lg font-bold rounded-xl border border-glass-border text-text-light hover:bg-white/5 transition-colors">Register Free</Link>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
        <h2 className="text-3xl font-bold gradient-text text-center mb-10">Choose Your Level & Begin</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
          {[
            { icon: '🟢', label: 'Easy Level', desc: 'Basic logical reasoning for beginners' },
            { icon: '🟡', label: 'Moderate Level', desc: 'Intermediate analytical reasoning' },
            { icon: '🔴', label: 'Advanced Level', desc: 'Complex problems for competitive exams' },
          ].map(l => (
            <button key={l.label} onClick={handleStart} className="glass-card text-center hover:border-primary/50 transition-colors">
              <div className="text-4xl mb-3">{l.icon}</div>
              <h3 className="text-text-light font-bold mb-2">{l.label}</h3>
              <p className="text-text-muted text-sm">{l.desc}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
        <h2 className="text-3xl font-bold gradient-text text-center mb-10">Why Take a Free Reasoning Test?</h2>
        <div className="glass-card max-w-3xl mx-auto">
          <p className="text-text-muted mb-4 text-sm">Reasoning is a key section in CUET UG & PG, UGC NET/JRF, Banking, SSC & other exams.</p>
          <div className="grid grid-cols-2 gap-3">
            {['Improve problem-solving speed', 'Enhance logical thinking', 'Increase accuracy', 'Reduce mistakes in exams'].map(b => (
              <div key={b} className="flex items-center gap-2 p-3 bg-bg-secondary rounded-xl text-sm">
                <span className="text-green-400">✔</span><span className="text-text-light">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
        <div className="glass-card max-w-2xl mx-auto text-center">
          <h2 className="text-xl font-bold text-text-light mb-6">🎯 Ideal for All Aspirants</h2>
          <div className="flex flex-wrap gap-3 justify-center">
            {['CUET UG', 'CUET PG', 'UGC NET', 'Banking Exams', 'SSC', 'Beginners to Advanced'].map(t => (
              <span key={t} className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-semibold">{t}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
        <h2 className="text-2xl font-bold gradient-text text-center mb-6">Boost Your Preparation Further</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
          {[{ label: 'Mock Tests', href: '/test-zone', icon: '🧪' }, { label: 'Current Affairs', href: '/current-affairs-test', icon: '🌍' }, { label: 'Study Materials', href: '/study-materials', icon: '📚' }, { label: 'Self-Paced Courses', href: '/self-paced', icon: '🎥' }].map(l => (
            <Link key={l.label} to={l.href} className="glass-card text-center hover:border-primary/50 transition-colors p-4">
              <div className="text-3xl mb-2">{l.icon}</div>
              <div className="text-text-light text-sm font-semibold">{l.label}</div>
            </Link>
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
        <h2 className="text-2xl font-bold gradient-text text-center mb-8">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((f, i) => (
            <details key={i} className="glass-card">
              <summary className="font-semibold text-text-light cursor-pointer list-none flex justify-between items-center">{f.q} <span className="text-primary text-xl">+</span></summary>
              <p className="text-text-muted mt-3 text-sm">{f.a}</p>
            </details>
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
        <div className="glass-card text-center max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-text-light mb-3">Master Reasoning with Daily Practice</h2>
          <p className="text-text-muted mb-6">Start practicing today and improve your logical thinking skills.</p>
          <button onClick={handleStart} className="btn-primary px-8 py-3 font-bold rounded-xl">🚀 Start Free Reasoning Test</button>
        </div>
      </div>
    </div>
  );
};

export default ReasoningTestPage;
